/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { MoengageSdk } from '../types/moengage';

// TEMPORARY (PLATFORM-26035) — test-only stand-in for the GTM MoEngage tag.
// REMOVE BEFORE MERGE. In production MoEngage must only ever be loaded by GTM.

const SDK_URL =
  'https://cdn.moengage.com/release/dc_2/moe_webSdk.min.latest.js';

// Shared MoEngage workspace (see apps/move-app/docs/MOENGAGE_INTEGRATION.md),
// used here in its TEST environment only.
const TEST_WORKSPACE_ID = '04HE359MZV4QO9K0VAJ61D31';

// Hosts allowed to run this. Production hosts never match, so a misconfigured
// NEXT_PUBLIC_ENV still cannot turn it on in prd.
const TEST_HOST_PATTERN =
  /(^|\.)(devairasia\.com|stgairasia\.com|run\.app)$|^localhost$|^127\.0\.0\.1$/;

// Hosts where home-web serves the page itself and ships /serviceworker.js.
// Everywhere else the shared /service-worker.js comes from the static bucket.
const HOME_WEB_DIRECT_HOST_PATTERN =
  /^home\.devairasia\.com$|\.run\.app$|^localhost$|^127\.0\.0\.1$/;

type MoeInit = (config: {
  appId: string;
  cluster: string;
  env: 'TEST';
  logLevel: number;
  swPath: string;
}) => MoengageSdk;

const isTestHost = (hostname: string) => TEST_HOST_PATTERN.test(hostname);

// Round rubber-stamp seal, tilted, fixed top-left and click-through.
const SEAL_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="132" height="132">
  <defs>
    <path id="moe-seal-top" d="M 30 100 A 70 70 0 0 1 170 100" />
    <path id="moe-seal-bottom" d="M 22 100 A 78 78 0 0 0 178 100" />
  </defs>
  <g fill="none" stroke="#c8102e">
    <circle cx="100" cy="100" r="94" stroke-width="5" />
    <circle cx="100" cy="100" r="84" stroke-width="2" />
    <circle cx="100" cy="100" r="54" stroke-width="2" />
  </g>
  <g fill="#c8102e" font-family="system-ui, sans-serif" font-weight="800">
    <text font-size="16" letter-spacing="2">
      <textPath href="#moe-seal-top" startOffset="50%" text-anchor="middle">PLATFORM-26035</textPath>
    </text>
    <text font-size="12" letter-spacing="1.5">
      <textPath href="#moe-seal-bottom" startOffset="50%" text-anchor="middle">GAURAV · MOENGAGE TEST</textPath>
    </text>
    <text x="100" y="94" font-size="30" text-anchor="middle">TEST</text>
    <text x="100" y="122" font-size="22" text-anchor="middle">BUILD</text>
  </g>
</svg>`;

const showTestWatermark = () => {
  if (document.getElementById('moe-test-watermark')) return;
  const seal = document.createElement('div');
  seal.id = 'moe-test-watermark';
  seal.setAttribute('aria-hidden', 'true');
  seal.innerHTML = SEAL_SVG;
  Object.assign(seal.style, {
    position: 'fixed',
    left: '12px',
    top: '96px',
    zIndex: '2147483647',
    opacity: '0.75',
    transform: 'rotate(-14deg)',
    pointerEvents: 'none',
    mixBlendMode: 'multiply',
  });
  document.body.appendChild(seal);
};

/**
 * Loads and initialises the MoEngage web SDK directly (TEST environment),
 * bypassing GTM, and shows a "TEST BUILD" watermark.
 *
 * No-op unless ALL of these hold, so it cannot run in production:
 * - `NEXT_PUBLIC_ENV` is not `prd`
 * - the page is on a dev/test host (devairasia, stgairasia, run.app, localhost)
 * - nothing (e.g. GTM) has already put `window.Moengage` on the page
 */
export const loadMoengageSdkForTesting = () => {
  if (typeof window === 'undefined') return;
  if (process.env.NEXT_PUBLIC_ENV === 'prd') return;

  const { hostname } = window.location;
  if (!isTestHost(hostname)) return;

  showTestWatermark();

  if (window.Moengage) return;
  if (document.querySelector(`script[src="${SDK_URL}"]`)) return;

  const swPath = HOME_WEB_DIRECT_HOST_PATTERN.test(hostname)
    ? '/serviceworker.js'
    : '/service-worker.js';

  const script = document.createElement('script');
  script.src = SDK_URL;
  script.async = true;
  script.onload = () => {
    const moe = (window as unknown as { moe?: MoeInit }).moe;
    if (window.Moengage || !moe) return;
    // logLevel 2 so the SDK's own console output is visible while testing.
    window.Moengage = moe({
      appId: TEST_WORKSPACE_ID,
      cluster: 'DC_2',
      env: 'TEST',
      logLevel: 2,
      swPath,
    });
  };
  document.head.appendChild(script);
};
